import React, { createContext, useContext, useState, useCallback } from "react";
import { useSelector } from "react-redux";
import DemoActionModal from "../components/DemoActionModal";
import { categoryToSlug } from "../utils/crmRoutes";

/**
 * Demo write-guard.
 *
 * A demo is a *tour* of static, fake sample data — somewhere to click
 * around and see how Stacked would look. It is NOT a scratch account a
 * visitor can load their own real data into: adding contacts/properties,
 * changing account info, or connecting their Google/Gmail all imply a
 * real, owned CRM, which the demo isn't. (Converting later buys a fresh
 * CRM — they never "own" the demo.)
 *
 * Rather than scatter `if (isDemo) toast(...)` across dozens of write
 * handlers, callers wrap the action:
 *
 *   const { blockDemoAction } = useDemoGuard();
 *   const handleSave = () => {
 *     if (blockDemoAction({ action: "Saving your profile" })) return;
 *     ...real save...
 *   };
 *
 * `blockDemoAction` returns true (and pops the "just a demo" modal) only
 * for demo sessions; for everyone else it returns false and the action
 * proceeds untouched. The default context value also returns false, so a
 * component rendered outside the provider degrades safe (never blocks).
 */

const DemoGuardContext = createContext({
  isDemo: false,
  demoVertical: "realestate",
  blockDemoAction: () => false,
});

export function DemoGuardProvider({ children }) {
  const isDemo = useSelector((s) => Boolean(s.users?.user?.isDemoAccount));
  // Which CRM this demo session belongs to ('realestate' | 'wholesale' |
  // 'probate' | 'flipper'), derived from the demo user's plan category.
  // Sample-data fallbacks that hardcode real-estate content must check
  // this — every vertical's demo is its own isolated world, and RE
  // names/showings/tasks must never surface inside another vertical.
  const planCategory = useSelector((s) => s.users?.user?.plan?.category);
  const demoVertical = categoryToSlug[planCategory] || "realestate";
  // `null` = closed; otherwise the optional action label to name in the modal.
  const [blockedAction, setBlockedAction] = useState(null);
  const [open, setOpen] = useState(false);

  const blockDemoAction = useCallback(
    (opts = {}) => {
      if (!isDemo) return false;
      setBlockedAction(opts.action || null);
      setOpen(true);
      return true;
    },
    [isDemo]
  );

  return (
    <DemoGuardContext.Provider value={{ isDemo, demoVertical, blockDemoAction }}>
      {children}
      <DemoActionModal
        open={open}
        action={blockedAction}
        onClose={() => setOpen(false)}
      />
    </DemoGuardContext.Provider>
  );
}

export const useDemoGuard = () => useContext(DemoGuardContext);
